import { Component, OnInit } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { Router } from '@angular/router'

import { flashMessages } from '../../../../../../locale/flash-messages/flash-messages.en'
import { metaDataContent } from '../../../../../../locale/meta-data/meta-data.content.en'
import { appConstants } from '../../../../../app.constants'
import { ResourceSlug } from '../../../../../shared/enums/wordpress/wp-resource-slug.enum'
import { BreadcrumbLink } from '../../../../../shared/interfaces/breadcrumb-link.interface'
import { SelectOption } from '../../../../../shared/interfaces/select-option.interface'
import { BreadcrumbService } from '../../../../../shared/services/breadcrumb.service'
import { CatalogueService } from '../../../../../shared/services/catalogue.service'
import { FlashMessageService } from '../../../../../shared/services/flash-message.service'
import { MetaService } from '../../../../../shared/services/meta.service'
import { Metric } from '../../../interfaces/metric.interface'
import { WordCountValidators } from '../../../partials/rich-text-input/word-count.validator'
import { MetricUseCaseService } from '../../../services/metric-use-case.service'
import { MetricService } from '../../../services/metric.service'
import { MetricUseCaseDto } from '../dtos/metric-use-case.dto'

@Component({
  selector: 'app-metric-create',
  templateUrl: './metric-use-case-create.component.html',
  styleUrls: ['./metric-use-case-create.component.scss']
})
export class MetricUseCaseCreateComponent implements OnInit {
  createTaxonomyFunction: (
    slug: string,
    name: string
  ) => Promise<SelectOption[]> = (slug: string, name: string) =>
    this.catalogueService.store(slug, { name })

  breadcrumbLinks: BreadcrumbLink[] = []

  taxonomyGroups: {
    name: string
    description?: string
    taxonomies: {
      label: string
      placeholder: string
      slug: ResourceSlug
      propName: string
      maxSelectedItems?: number
      options?: SelectOption[]
      createFunction?: (name: string) => Promise<SelectOption[]>
    }[]
  }[] = [
    {
      name: 'Context of the use case',
      taxonomies: [
        {
          label: 'Objectives (max. 3)',
          propName: 'objectiveIds',
          placeholder: `Select the objective(s) covered by the use case...`,
          slug: ResourceSlug.Objectives,
          maxSelectedItems: 3
        },
        {
          label: 'Purposes (max. 3)',
          propName: 'purposeIds',
          placeholder: 'Purpose or action of the AI system..',
          slug: ResourceSlug.Purposes,
          maxSelectedItems: 3,
          createFunction: (newTermName: string) =>
            this.createTaxonomyFunction(
              `taxonomies/${ResourceSlug.Purposes}`,
              newTermName
            )
        },
        {
          label: 'Sectors (max. 3)',
          propName: 'targetSectorIds',
          placeholder: 'Select the sector(s) covered by the use case...',
          slug: ResourceSlug.TargetSectors,
          maxSelectedItems: 3
        }
      ]
    }
  ]

  form: FormGroup

  metric: Metric
  metricUseCaseDto: MetricUseCaseDto
  captchaKey = appConstants.RECAPTCHA_SITE_KEY
  showErrors = false
  invalidInputs: string[]
  isRelatedPostInvalid = false
  loadingSubmit = false

  searchMetricFunction: (
    terms: string
  ) => Promise<{ id: number; label: string }[]>

  constructor(
    private metricService: MetricService,
    private metricUseCaseService: MetricUseCaseService,
    private formBuilder: FormBuilder,
    private catalogueService: CatalogueService,
    private flashMessageService: FlashMessageService,
    private router: Router,
    breadcrumbService: BreadcrumbService,
    metaService: MetaService
  ) {
    this.breadcrumbLinks = breadcrumbService.getBreadCrumbLinks('toolCatalog', [
      {
        label: 'Metric use cases',
        path: '/catalogue/metric-use-cases'
      },
      {
        label: 'Submit a use case for a metric'
      }
    ])
    this.searchMetricFunction = (terms: string) =>
      this.metricService.listUnpaginated({ terms }).then((res: Metric[]) =>
        res.map((metric: Metric) => ({
          id: metric.id,
          label: metric.name
        }))
      )
    metaService.setTags(metaDataContent.toolCatalogMetricUseCaseSubmit)
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      title: ['', [Validators.required]],
      content: ['', [Validators.required, WordCountValidators.max(1000)]],
      mainReferenceLink: '',
      websiteLink: '',
      githubLink: '',
      huggingFaceLink: '',
      email: ['', [Validators.required, Validators.email]],
      submitter: '',
      metricIds: [null, Validators.required],
      // Validation and consent.
      recaptchaReactive: [null, Validators.required],
      consent: [false, Validators.requiredTrue],
      consentPublicationDelay: [false, Validators.requiredTrue]
    })

    this.taxonomyGroups.forEach((taxonomyGroup) => {
      taxonomyGroup.taxonomies.forEach((taxonomy) => {
        // Add dynamic form control.
        this.form.addControl(taxonomy.propName, new FormControl([]))

        this.catalogueService
          .listSelectOptions(taxonomy.slug)
          .then((filterTaxonomyRes: SelectOption[]) => {
            taxonomy.options = filterTaxonomyRes
          })
      })
    })
  }

  onTaxonomySelected(event: (string | number)[], formControlName: string) {
    this.form.get(formControlName).setValue(event)
  }

  onMetricSelected(metricIds: number[]) {
    this.form.get('metricIds').setValue(metricIds)
  }

  submit(formValue) {
    if (this.form.invalid) {
      this.invalidInputs = this.debugFindInvalidControls()
      this.showErrors = true
      return this.flashMessageService.error(
        'Please fill in all required fields and click on the CAPTCHA to submit the metric use case.'
      )
    }

    this.loadingSubmit = true

    const metricUseCaseDto: MetricUseCaseDto = {
      name: formValue.title,
      content: formValue.content,
      mainReferenceLink: formValue.mainReferenceLink,
      websiteLink: formValue.websiteLink,
      githubLink: formValue.githubLink,
      huggingFaceLink: formValue.huggingFaceLink,
      email: formValue.email,
      submitter: formValue.submitter,
      metricIds: formValue.metricIds,
      objectiveIds: formValue.objectiveIds,
      targetSectorIds: formValue.targetSectorIds,
      purposeIds: formValue.purposeIds
    }

    // Apply dynamic taxonomy input values to metricUseCaseDto.
    this.taxonomyGroups.forEach((taxonomyGroup) => {
      taxonomyGroup.taxonomies.forEach((taxonomy) => {
        if (this.form.value[taxonomy.propName]) {
          metricUseCaseDto[taxonomy.propName] =
            this.form.value[taxonomy.propName]
        }
      })
    })

    this.metricUseCaseService.store(metricUseCaseDto).then(
      async () => {
        this.router.navigate(['/catalogue/metric-use-cases/submit/success'])
        this.loadingSubmit = false
      },

      () => {
        // TODO: new flash messages for other entities than tools.
        this.flashMessageService.error(flashMessages.tool.error.cannotSend)
        this.loadingSubmit = false
      }
    )
  }

  onRichTextChanged(wysiwygInputValue: string, formControlName: string) {
    this.form.get(formControlName).setValue(wysiwygInputValue)
  }

  private debugFindInvalidControls(): string[] {
    const invalid = []
    const controls = this.form.controls
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(name)
      }
    }
    return invalid
  }
}
